/*
 * СЦЕНАРИИ ТЕОРИИ — кадры мультиков по урокам курса «Первые детали».
 *
 * Формат и правила — из общего решения по обучающим мультикам
 * (БЭКАПЫ/HANDOFF_2026_08_10_NOVYY_FORMAT_MULTIKOV.md):
 *
 *   act(...)  — ПРОЦЕДУРА: действие в интерфейсе. Анимируется.
 *               Метаанализ Höffler & Leutner: d ≈ 1.06 — очень большой эффект.
 *   idea(...) — КОНЦЕПЦИЯ: что это и зачем. СТАТИЧНАЯ схема.
 *               Для концепций анимация почти не помогает (d ≈ 0.37) и вредит
 *               транзиторностью: информация исчезает с экрана.
 *   result(...) — что получилось.
 *
 * ⚠️ Действия собираются через doAction() — она даёт правильный темп
 * «подсветить → пауза → доехать → клик → пауза». Руками шаги не набирать,
 * иначе темп разъедется между уроками.
 *
 * Длительность кадра НЕ проставляется: её задаёт озвучка (модель Duolingo),
 * см. lesson() в scenario.js.
 */

import {
    act, idea, result, lesson,
    doAction, moveTo, openPanel, spawn, select,
    setProp, orbit, axes, note, beat,
} from './scenario';


/* ═══ УРОК 1. Пространство и виды ═════════════════════════════════════ */

export const LESSON_SPACE = lesson([

    idea('Вот окно редактора. Сверху лента с инструментами, слева браузер '
        + 'документа, справа свойства, а большое светлое поле в центре — сцена. '
        + 'Там и живёт деталь.', {
        diagram: 'layout',
        labels: [
            { at: 'ribbon', text: 'здесь все команды' },
            { at: 'scene', text: 'здесь деталь' },
        ],
    }),

    idea('В пространстве три направления — их называют осями. Красная X идёт '
        + 'вправо, зелёная Y вглубь, а синяя Z смотрит вверх. По оси Z растёт '
        + 'высота детали.', {
        diagram: 'axes',
        labels: [
            { at: 'z', text: 'высота' },
            { at: 'x', text: 'вправо' },
            { at: 'y', text: 'вглубь' },
        ],
    }),

    act('Деталь надо разглядывать со всех сторон. Зажми правую кнопку мыши '
        + 'и веди в сторону — камера облетит деталь по кругу.', {
        layout: 'full',
        steps: [
            axes(true),
            spawn('part', { w: 40, d: 30, h: 20, label: 'Тело 1' }),
            beat(),
            moveTo('scene'),
            // Облёт в обе стороны: видно, что это именно поворот камеры,
            // а не сдвиг детали.
            orbit(38),
            orbit(-24),
            note('камера облетает деталь', { at: 'scene' }),
        ],
    }),

    act('Если нужен ровный вид, не крути мышью. Щёлкни по грани '
        + 'куба-навигатора в углу — камера встанет точно спереди или сверху.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 40, d: 30, h: 20, label: 'Тело 1' }),
            ...doAction('viewcube'),
            orbit(0),
            note('вид стал ровным', { at: 'scene' }),
        ],
    }),

    idea('Любая деталь начинается с плоской фигуры — эскиза. Его рисуют '
        + 'на плоскости, а потом поднимают в объём. Это главный приём '
        + 'всего моделирования.', {
        diagram: 'sketch-solid',
    }),

], { id: 'fp-01-space' });


/* ═══ УРОК 2. Эскиз и размеры ═════════════════════════════════════════ */

export const LESSON_SKETCH = lesson([

    idea('Эскиз рисуют не в воздухе, а на плоскости. Их три: XY — это вид '
        + 'сверху, XZ — спереди, YZ — сбоку. Выбирают ту, на которой '
        + 'фигуру удобнее нарисовать.', {
        diagram: 'planes',
    }),

    act('Нажми зелёную кнопку «Эскиз» — самую левую на ленте.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('planes'),
            note('редактор просит выбрать плоскость', { at: 'scene' }),
        ],
    }),

    act('Щёлкни по плоскости — она станет твоим листом бумаги. Лента '
        + 'сменится: появятся линия, прямоугольник, окружность и размеры.', {
        layout: 'full',
        steps: [
            openPanel('planes'),
            ...doAction('plane-xy'),
            openPanel('sketch-mode'),
            beat(),
        ],
    }),

    act('Возьми прямоугольник и протяни его мышью. Пока это набросок — '
        + 'размеры примерные.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            spawn('sk', { w: 40, d: 30, h: 0.6, label: 'Эскиз 1' }),
            note('фигура нарисована на глаз', { at: 'scene' }),
        ],
    }),

    act('Теперь главное: нажми «Размер» и впиши точное число. '
        + 'Сорок миллиметров — и сторона станет ровно сорок.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 40, d: 30, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-dim'),
            setProp('Длина', '40'),
            beat(),
            setProp('Ширина', '30'),
            note('теперь эскиз закреплён', { at: 'scene' }),
        ],
    }),

    idea('Зачем это нужно. Фигура без размеров поедет при первой же правке: '
        + 'потянешь одну линию — сдвинется половина рисунка. Числа держат '
        + 'эскиз на месте.', {
        diagram: 'dims',
        labels: [{ note: 'размеры держат фигуру' }],
    }),

    act('Закрой эскиз кнопкой справа — вернёшься в объёмный режим, '
        + 'и лента снова станет обычной.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 40, d: 30, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            beat(),
        ],
    }),

], { id: 'fp-02-sketch' });


/* ═══ УРОК 3. Выдавливание и вырез ════════════════════════════════════ */

export const LESSON_EXTRUDE = lesson([

    act('Эскиз готов — нажми «Выдавить» в группе «Создать».', {
        layout: 'full',
        steps: [
            spawn('sk', { w: 40, d: 30, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('tool-extrude'),
            note('редактор спрашивает высоту', { at: 'scene' }),
        ],
    }),

    act('Впиши высоту — двадцать миллиметров. Плоская фигура на глазах '
        + 'превращается в объёмную деталь.', {
        layout: 'full',
        steps: [
            spawn('sk', { w: 40, d: 30, h: 0.6, label: 'Эскиз 1' }),
            setProp('Высота', '20'),
            beat(),
            spawn('part', { w: 40, d: 30, h: 20, label: 'Тело 1' }),
            select('part'),
            note('в браузере появилось «Тело 1»', { at: 'scene' }),
        ],
    }),

    idea('У выдавливания есть второй режим — вырез. Та же фигура тогда не '
        + 'добавляет материал, а прогрызает его. Так делают пазы и окна.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'фигура на грани' },
            { at: 'solid', text: 'вырезали углубление' },
        ],
    }),

    act('Эскиз для выреза рисуют прямо на грани детали: щёлкни по грани, '
        + 'потом «Эскиз» — и рисуй на ней, как на бумаге.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 40, d: 30, h: 20, label: 'Тело 1' }),
            ...doAction('scene'),
            select('part'),
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            // Деталь уже с углублением — это результат урока
            spawn('cut1', { w: 40, d: 30, h: 20,
                cut: { w: 24, d: 16, depth: 8 }, label: 'Вырез' }),
            note('углубление вырезано', { at: 'scene' }),
        ],
    }),

    result('Готово: из плоского прямоугольника получилась деталь '
        + 'с углублением. Теперь попробуй сам.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 40, d: 30, h: 20, label: 'Тело 1' }),
            select('part'),
            orbit(28),
            // Пауза после облёта: без неё кадр обрывается на движении,
            // и ребёнок не успевает рассмотреть готовую деталь.
            note('деталь готова', { at: 'scene' }),
        ],
    }),

], { id: 'fp-03-extrude' });


/* ═══ УРОК 4. Брелок с именем ═════════════════════════════════════════ */

export const LESSON_KEYCHAIN = lesson([

    idea('Сегодня первая настоящая вещь — брелок, который можно распечатать '
        + 'и носить с собой. В нём сразу три приёма: пластина, скруглённые '
        + 'углы и текст.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'пластина 50 × 20' },
            { at: 'solid', text: 'толщина 4 мм' },
        ],
    }),

    /*
     * ⚠️ Одно действие — один кадр. Соблазн уместить «эскиз → размеры →
     * закрыть → выдавить» в одну реплику велик, но такой кадр идёт долго,
     * а диктор успевает сказать только про начало: ребёнок смотрит на
     * действия, которые уже не комментируются.
     */
    act('Начинаем как обычно: эскиз, прямоугольник пятьдесят на двадцать.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '50'),
            setProp('Ширина', '20'),
            note('пластина размечена', { at: 'scene' }),
        ],
    }),

    act('Закрываем эскиз и выдавливаем на четыре миллиметра — брелок '
        + 'должен быть тонким.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 50, d: 20, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '4'),
            spawn('plate', { w: 50, d: 20, h: 4, label: 'Тело 1' }),
            note('пластина готова', { at: 'scene' }),
        ],
    }),

    act('Теперь скруглим углы, чтобы брелок не колол карман. Выбери '
        + 'вертикальные рёбра и нажми «Скругление», радиус четыре.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 50, d: 20, h: 4, fillet: 4, label: 'Тело 1' }),
            ...doAction('scene'),
            select('plate'),
            ...doAction('tool-fillet'),
            setProp('Радиус', '4'),
            note('углы скруглены', { at: 'scene' }),
        ],
    }),

    act('Отверстие под кольцо: щёлкни по верхней грани, «Отверстие», '
        + 'диаметр четыре миллиметра. Ставь его у края, но не вплотную.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 50, d: 20, h: 4, fillet: 4,
                holes: [{ x: -19, y: 0, r: 2 }], label: 'Тело 1' }),
            ...doAction('scene'),
            select('plate'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '4'),
            note('отверстие под кольцо', { at: 'scene' }),
        ],
    }),

    act('И самое приятное — имя. В режиме эскиза есть «Текст»: напиши своё '
        + 'имя и выдави буквы на миллиметр наружу.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 50, d: 20, h: 4, fillet: 4,
                holes: [{ x: -19, y: 0, r: 2 }], text: 'МИН', label: 'Тело 1' }),
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-text'),
            setProp('Текст', 'МИН'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '1'),
            note('буквы стали выпуклыми', { at: 'scene' }),
        ],
    }),

    result('Брелок готов: пластина, скруглённые углы, отверстие под кольцо '
        + 'и своё имя. Теперь сделай такой же со своим именем.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 50, d: 20, h: 4, fillet: 4,
                holes: [{ x: -19, y: 0, r: 2 }], text: 'МИН', label: 'Брелок' }),
            select('plate'),
            orbit(32),
            note('можно печатать', { at: 'scene' }),
        ],
    }),

], { id: 'fp-04-keychain' });


/* ═══ УРОК 5. Скругление и фаска ══════════════════════════════════════ */

export const LESSON_FILLET = lesson([

    idea('Острый угол — слабое место детали. Вся нагрузка собирается в одной '
        + 'точке, и ломается деталь именно там. Скругление разводит нагрузку '
        + 'по дуге, и деталь держит заметно больше.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'острый угол — трещина' },
            { at: 'solid', text: 'скруглённый — держит' },
        ],
    }),

    act('Скругление ставят на готовой детали. Выбери ребро в сцене, нажми '
        + '«Скругление» и задай радиус шесть миллиметров.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 40, d: 40, h: 20, fillet: 6, label: 'Тело 1' }),
            ...doAction('scene'),
            select('part'),
            ...doAction('tool-fillet'),
            setProp('Радиус', '6'),
            note('ребро стало круглым', { at: 'scene' }),
        ],
    }),

    idea('Фаска — другое: это срезанный под углом край. Её ставят не для '
        + 'прочности, а чтобы деталь легче входила в отверстие и не резала '
        + 'руки.', {
        diagram: 'dims',
        labels: [{ at: 'note', text: 'фаска срезает край под углом' }],
    }),

    act('Фаска делается так же: выбери верхний контур, нажми «Фаска», '
        + 'задай два миллиметра.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 40, d: 40, h: 20, fillet: 6, chamfer: 2,
                label: 'Тело 1' }),
            ...doAction('scene'),
            select('part'),
            ...doAction('tool-chamfer'),
            setProp('Размер', '2'),
            note('край срезан', { at: 'scene' }),
        ],
    }),

    idea('Простое правило: внутренний угол, где может треснуть — скругление. '
        + 'Наружный край, за который берутся руками или который куда-то '
        + 'вставляют — фаска.', {
        diagram: 'layout',
        labels: [
            { at: 'scene', text: 'скругление — прочность, фаска — удобство' },
        ],
    }),

], { id: 'fp-05-fillet' });


/* ═══ УРОК 6. Отверстия под винты ═════════════════════════════════════ */

export const LESSON_HOLES = lesson([

    idea('Отверстие под винт делают чуть больше самого винта, иначе он не '
        + 'войдёт. Для винта М3 сверлят три и три десятых миллиметра, '
        + 'для М4 — четыре и три.', {
        diagram: 'dims',
        labels: [
            { at: 'w', text: 'Ø3,3' },
            { at: 'note', text: 'отверстие всегда чуть больше винта' },
        ],
    }),

    act('Сделаем пластину под крепёж. Эскиз, прямоугольник восемьдесят '
        + 'на шестьдесят.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '80'),
            setProp('Ширина', '60'),
            note('контур размечен', { at: 'scene' }),
        ],
    }),

    act('Закрываем эскиз и выдавливаем на шесть миллиметров.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 80, d: 60, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '6'),
            spawn('plate', { w: 80, d: 60, h: 6, label: 'Тело 1' }),
            note('основание готово', { at: 'scene' }),
        ],
    }),

    act('Теперь отверстия. Щёлкни по верхней грани, нажми «Отверстие» '
        + 'и задай диаметр три и три.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 80, d: 60, h: 6,
                holes: [{ x: -30, y: -20, r: 1.65 }, { x: 30, y: -20, r: 1.65 },
                    { x: -30, y: 20, r: 1.65 }, { x: 30, y: 20, r: 1.65 }],
                label: 'Тело 1' }),
            ...doAction('scene'),
            select('plate'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '3.3'),
            setProp('Тип', 'насквозь'),
            note('отверстие сквозное', { at: 'scene' }),
        ],
    }),

    idea('Сквозное отверстие проходит деталь насквозь, глухое имеет дно — '
        + 'ему задают глубину. И запомни: если отверстие ближе трёх своих '
        + 'диаметров к краю, стенка треснет при затяжке винта.', {
        diagram: 'dims',
        labels: [
            { at: 'h', text: '10' },
            { at: 'note', text: 'отступ от края — не меньше трёх диаметров' },
        ],
    }),

    result('Четыре отверстия по углам обычно рисуют одним эскизом — так они '
        + 'точно встанут симметрично.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 80, d: 60, h: 6,
                holes: [{ x: -30, y: -20, r: 1.65 }, { x: 30, y: -20, r: 1.65 },
                    { x: -30, y: 20, r: 1.65 }, { x: 30, y: 20, r: 1.65 }],
                label: 'Тело 1' }),
            select('plate'),
            orbit(26),
            note('пластина под крепёж готова', { at: 'scene' }),
        ],
    }),

], { id: 'fp-06-holes' });


/* ═══ УРОК 7. Оболочка ════════════════════════════════════════════════ */

export const LESSON_SHELL = lesson([

    idea('Коробку можно сделать вырезом: построить брусок и выгрызть '
        + 'внутренность. Но стенки тогда придётся считать вручную, а при '
        + 'изменении размеров — пересчитывать заново.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'сплошной брусок' },
            { at: 'solid', text: 'нужна полость' },
        ],
    }),

    act('Оболочка делает это сама. Сначала обычный брусок: эскиз '
        + 'семьдесят на пятьдесят.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '70'),
            setProp('Ширина', '50'),
            note('основание коробки', { at: 'scene' }),
        ],
    }),

    act('Выдавливаем на тридцать миллиметров — получился сплошной брусок.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 60, d: 40, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '30'),
            spawn('box', { w: 70, d: 50, h: 30, label: 'Тело 1' }),
            note('брусок готов', { at: 'scene' }),
        ],
    }),

    act('Теперь выбери верхнюю грань — ту, которую надо убрать, — нажми '
        + '«Оболочка» и задай толщину стенки два миллиметра.', {
        layout: 'full',
        steps: [
            spawn('box', { w: 70, d: 50, h: 30, shell: 2, label: 'Тело 1' }),
            ...doAction('scene'),
            select('box'),
            ...doAction('tool-shell'),
            setProp('Толщина', '2'),
            note('внутри стало пусто', { at: 'scene' }),
        ],
    }),

    idea('Главная выгода: изменишь габариты — стенки останутся ровно два '
        + 'миллиметра, пересчитывать ничего не надо. А для печати тоньше '
        + 'полутора миллиметров делать нельзя: будет хрупко.', {
        diagram: 'dims',
        labels: [
            { at: 'note', text: 'стенка 2 мм держится при любых габаритах' },
        ],
    }),

    result('Из бруска получилась коробка без крышки — за одну операцию '
        + 'вместо десятка вырезов.', {
        layout: 'full',
        steps: [
            spawn('box', { w: 70, d: 50, h: 30, shell: 2, label: 'Коробка' }),
            select('box'),
            orbit(34),
            note('коробка готова', { at: 'scene' }),
        ],
    }),

], { id: 'fp-07-shell' });


/* ═══ УРОК 8. Проект: подставка под телефон ═══════════════════════════ */

export const LESSON_STAND = lesson([

    idea('Итоговая работа курса. Здесь нет пошаговой инструкции: есть '
        + 'требования, а как их выполнить — решаешь сам. Это и есть '
        + 'проектирование.', {
        diagram: 'layout',
        labels: [
            { at: 'scene', text: 'твоя подставка' },
            { at: 'right', text: 'здесь размеры' },
        ],
    }),

    idea('Подставка должна держать телефон под наклоном и не опрокидываться. '
        + 'Удобный наклон — шестьдесят-семьдесят градусов от стола: меньше — '
        + 'экран запрокинут, больше — телефон соскальзывает.', {
        diagram: 'planes',
        labels: [
            { at: 'xz', text: 'профиль рисуют сбоку' },
            { at: 'note', text: 'наклон спинки 60–70°' },
        ],
    }),

    act('Самый простой путь — нарисовать профиль сбоку и выдавить его '
        + 'на ширину. Возьми плоскость XZ и нарисуй боковой контур.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('planes'),
            // Профиль подставки рисуют СБОКУ — это плоскость XZ, не XY.
            ...doAction('plane-xz'),
            openPanel('sketch-mode'),
            ...doAction('sk-line'),
            spawn('prof', { w: 70, d: 60, h: 0.6, label: 'Эскиз 1' }),
            note('профиль подставки', { at: 'scene' }),
        ],
    }),

    act('Закрой эскиз и выдави профиль на ширину телефона — примерно '
        + 'восемьдесят миллиметров.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('prof', { w: 70, d: 60, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '80'),
            spawn('stand', { w: 70, d: 80, h: 60, label: 'Тело 1' }),
            note('подставка в объёме', { at: 'scene' }),
        ],
    }),

    idea('Осталось два штриха: паз под телефон шире его толщины на два '
        + 'миллиметра — на чехол, и скругления по краям, чтобы подставку '
        + 'было приятно брать в руки.', {
        diagram: 'dims',
        labels: [
            { at: 'note', text: 'паз = толщина телефона + 2 мм' },
        ],
    }),

    result('Измерь свой телефон линейкой и сделай подставку под него. '
        + 'Проверка смотрит только общие требования — остальное на твой вкус.', {
        layout: 'full',
        steps: [
            spawn('stand', { w: 70, d: 80, h: 60, label: 'Тело 1' }),
            select('stand'),
            orbit(30),
            note('удачи в проекте', { at: 'scene' }),
        ],
    }),

], { id: 'fp-08-stand' });


/* ═══════════════════════════════════════════════════════════════════════
 *  КУРС 2. «ЧИТАЕМ ЧЕРТЕЖИ»
 *
 *  Самая недооценённая тема. Приём взят из американской методики
 *  (TeachEngineering, «Block-n-Draw Relay»): понимание приходит от
 *  ПЕРЕКЛЮЧЕНИЯ «объём ↔ плоский чертёж», а не от объяснения. Поэтому
 *  здесь больше кадров-объяснений: чертёж — это концепция, её надо
 *  рассматривать, а не смотреть, как она проносится мимо.
 * ═══════════════════════════════════════════════════════════════════ */

/* ─── УРОК 9. Три взгляда на предмет ─────────────────────────────── */

export const LESSON_VIEWS = lesson([

    idea('Деталь объёмная, а бумага плоская. Чтобы записать деталь на бумаге, '
        + 'на неё смотрят с трёх сторон: спереди, сбоку и сверху. Каждый '
        + 'взгляд даёт свой плоский вид.', {
        diagram: 'views',
    }),

    idea('Важное: виды получаются разные. Спереди видно длину и высоту, '
        + 'сбоку — глубину и высоту, сверху — длину и глубину. Три вида '
        + 'вместе описывают деталь целиком.', {
        diagram: 'views',
        labels: [
            { at: 'front', text: 'длина и высота' },
            { at: 'side', text: 'глубина и высота' },
            { at: 'top', text: 'длина и глубина' },
            { at: 'note', text: 'каждый вид — свои две стороны' },
        ],
    }),

    act('В редакторе виды видно сразу: щёлкай по граням куба-навигатора, '
        + 'и камера встаёт то спереди, то сверху.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 50, d: 30, h: 20, label: 'Тело 1' }),
            ...doAction('viewcube'),
            orbit(0),
            note('вид спереди', { at: 'scene' }),
        ],
    }),

    act('Ещё щелчок — и та же деталь видна сверху. Это и есть вид сверху '
        + 'с чертежа, только живой.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 50, d: 30, h: 20, label: 'Тело 1' }),
            ...doAction('viewcube'),
            orbit(46),
            note('вид сверху', { at: 'scene' }),
        ],
    }),

    idea('Учись переключаться: посмотрел на деталь — представь её вид сверху. '
        + 'Посмотрел на чертёж — представь деталь. Это главный навык курса, '
        + 'и тренируется он только практикой.', {
        diagram: 'views',
        labels: [{ at: 'note', text: 'деталь и чертёж — туда и обратно' }],
    }),

], { id: 'dr-09-views' });


/* ─── УРОК 10. От детали к чертежу ───────────────────────────────── */

export const LESSON_TO_DRAWING = lesson([

    idea('Теперь наоборот: есть деталь, надо понять, как она выглядит '
        + 'на чертеже. Начни с простого — сними габариты.', {
        diagram: 'views',
        labels: [{ at: 'note', text: 'сначала длина, глубина, высота' }],
    }),

    act('Возьмём брусок сорок на тридцать на двадцать и посмотрим на него '
        + 'спереди.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 50, d: 30, h: 20, label: 'Тело 1' }),
            select('part'),
            ...doAction('viewcube'),
            orbit(0),
            note('спереди: сорок в ширину, двадцать в высоту', { at: 'scene' }),
        ],
    }),

    idea('Значит, вид спереди — прямоугольник сорок на двадцать. Вид сбоку — '
        + 'тридцать на двадцать. Вид сверху — сорок на тридцать. Ни одного '
        + 'лишнего числа, всё берётся из габаритов.', {
        diagram: 'dims',
        labels: [
            { at: 'w', text: '40' },
            { at: 'h', text: '20' },
            { at: 'd', text: '30' },
            { at: 'note', text: 'три габарита — три вида' },
        ],
    }),

    act('Проверь себя: обойди деталь навигатором и сверь каждый вид с тем, '
        + 'что представил.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 50, d: 30, h: 20, label: 'Тело 1' }),
            ...doAction('viewcube'),
            orbit(38),
            note('сверяй с тем, что представил', { at: 'scene' }),
        ],
    }),

], { id: 'dr-10-to-drawing' });


/* ─── УРОК 11. От чертежа к детали ───────────────────────────────── */

export const LESSON_FROM_DRAWING = lesson([

    idea('Обратная задача — самая важная. У тебя только чертёж, детали нет. '
        + 'Надо построить её в редакторе, ничего не увидев заранее.', {
        diagram: 'views',
        labels: [{ at: 'note', text: 'чертёж есть, детали нет — построй' }],
    }),

    idea('Порядок такой. Первое: по видам определи габариты. Второе: выбери, '
        + 'какой вид рисовать эскизом — обычно тот, где деталь сложнее. '
        + 'Третье: выдави на размер третьей стороны.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'сложный вид — в эскиз' },
            { at: 'solid', text: 'третий размер — высота' },
        ],
    }),

    act('Смотрим на чертёж: вид сверху — прямоугольник шестьдесят на сорок. '
        + 'Его и рисуем эскизом.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '60'),
            setProp('Ширина', '40'),
            note('размеры взяты прямо с чертежа', { at: 'scene' }),
        ],
    }),

    act('Вид спереди говорит: высота пятнадцать. Закрываем эскиз и '
        + 'выдавливаем на пятнадцать.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 60, d: 40, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '15'),
            spawn('part', { w: 60, d: 40, h: 15, label: 'Тело 1' }),
            note('деталь построена по чертежу', { at: 'scene' }),
        ],
    }),

    result('Готово. Теперь обойди её навигатором и сверь все три вида '
        + 'с чертежом — так проверяют себя настоящие конструкторы.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 60, d: 40, h: 15, label: 'Тело 1' }),
            select('part'),
            orbit(34),
            note('сверь виды с чертежом', { at: 'scene' }),
        ],
    }),

], { id: 'dr-11-from-drawing' });


/* ─── УРОК 12. Невидимые линии ───────────────────────────────────── */

export const LESSON_HIDDEN = lesson([

    idea('У детали бывает то, что снаружи не видно: отверстие, паз, полость. '
        + 'На чертеже это не прячут — рисуют штриховой линией.', {
        diagram: 'hidden',
    }),

    idea('Правило простое: сплошная линия — то, что видишь. Штриховая — '
        + 'то, что внутри и закрыто материалом. На одном виде отверстие '
        + 'сплошное, на другом — штриховое.', {
        diagram: 'hidden',
        labels: [
            { at: 'solid', text: 'сверху видно насквозь' },
            { at: 'hidden', text: 'спереди закрыто материалом' },
            { at: 'note', text: 'одно отверстие, два разных вида' },
        ],
    }),

    act('Сделаем деталь с отверстием и посмотрим на неё с двух сторон.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 40, d: 40, h: 20, label: 'Тело 1' }),
            ...doAction('scene'),
            select('part'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '12'),
            note('отверстие готово', { at: 'scene' }),
        ],
    }),

    act('Сверху отверстие видно как круг. Повернём на вид спереди — и оно '
        + 'исчезнет за материалом. Вот его и рисуют штриховой линией.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 40, d: 40, h: 20, label: 'Тело 1' }),
            ...doAction('viewcube'),
            orbit(0),
            note('спереди отверстия не видно', { at: 'scene' }),
        ],
    }),

], { id: 'dr-12-hidden' });


/* ─── УРОК 13. Размеры на чертеже ────────────────────────────────── */

export const LESSON_DIMS = lesson([

    idea('Чертёж без размеров — просто картинка. Размеры превращают её '
        + 'в документ, по которому деталь можно изготовить.', {
        diagram: 'dims',
        labels: [{ at: 'note', text: 'без чисел деталь не сделать' }],
    }),

    idea('Правила простые. Размер ставят к той стороне, которую меряют. '
        + 'И каждый размер указывают один раз: если длина есть на виде '
        + 'сверху, на виде спереди её не повторяют.', {
        diagram: 'dims',
        labels: [
            { at: 'w', text: '60' },
            { at: 'h', text: '15' },
            { at: 'd', text: '40' },
            { at: 'note', text: 'каждое число — ровно один раз' },
        ],
    }),

    idea('Диаметр отверстия пишут со значком диаметра, радиус скругления — '
        + 'с буквой Р. Так сразу понятно: двадцать поперёк круга или '
        + 'скругление радиусом шесть.', {
        diagram: 'hidden',
        labels: [
            { at: 'solid', text: 'диаметр 20' },
            { at: 'note', text: 'значок диаметра и буква Р — разные вещи' },
        ],
    }),

    act('В редакторе размеры ставят в эскизе — инструментом «Размер». '
        + 'Это те же числа, что потом окажутся на чертеже.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            ...doAction('sk-dim'),
            setProp('Длина', '60'),
            setProp('Ширина', '40'),
            note('размеры эскиза — это размеры чертежа', { at: 'scene' }),
        ],
    }),

], { id: 'dr-13-dims' });


/* ─── УРОК 14. Разрез ────────────────────────────────────────────── */

export const LESSON_SECTION = lesson([

    idea('Когда внутри детали много всего, штриховых линий становится столько, '
        + 'что чертёж не прочитать. Тогда деталь мысленно разрезают.', {
        diagram: 'section',
    }),

    idea('Разрез — это вид детали, у которой убрали переднюю половину. '
        + 'То, что было скрыто, становится видно сплошными линиями. '
        + 'А место, где прошёл нож, штрихуют косыми линиями.', {
        diagram: 'section',
        labels: [
            { at: 'whole', text: 'здесь проходит разрез' },
            { at: 'cut', text: 'штриховка — это материал в разрезе' },
            { at: 'note', text: 'штрихуют только то, что разрезали' },
        ],
    }),

    act('В редакторе то же самое видно на живой детали: сделай оболочку — '
        + 'и внутренность откроется без всякого чертежа.', {
        layout: 'full',
        steps: [
            spawn('box', { w: 60, d: 60, h: 40, shell: 3, label: 'Тело 1' }),
            ...doAction('scene'),
            select('box'),
            ...doAction('tool-shell'),
            setProp('Толщина', '2'),
            note('видно, что внутри', { at: 'scene' }),
        ],
    }),

    idea('Запомни разницу: штриховая линия — деталь целая, внутренность '
        + 'угадывается. Штриховка косыми линиями — деталь разрезана, '
        + 'внутренность видно точно.', {
        diagram: 'section',
        labels: [{ at: 'note', text: 'штриховая линия и штриховка — разное' }],
    }),

], { id: 'dr-14-section' });


/* ─── УРОК 15. Проект: деталь по чертежу ─────────────────────────── */

export const LESSON_DRAWING_PROJECT = lesson([

    idea('Итог курса. Даётся только чертёж: три вида с размерами, без '
        + 'картинки детали и без подсказок. Твоя задача — построить.', {
        diagram: 'views',
        labels: [{ at: 'note', text: 'только чертёж, дальше сам' }],
    }),

    idea('Порядок тот же, что в одиннадцатом уроке: сними габариты, выбери '
        + 'вид для эскиза, выдави на третий размер, потом добавь отверстия '
        + 'и скругления, если они есть на чертеже.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'первое — эскиз по главному виду' },
            { at: 'solid', text: 'второе — выдавить на третий размер' },
        ],
    }),

    act('Строим основание по виду сверху, как учились.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '70'),
            setProp('Ширина', '45'),
            note('габариты сняты с чертежа', { at: 'scene' }),
        ],
    }),

    act('Выдавливаем на высоту с вида спереди.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 60, d: 40, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '18'),
            spawn('part', { w: 80, d: 50, h: 18, label: 'Тело 1' }),
            note('основание готово', { at: 'scene' }),
        ],
    }),

    act('Добавляем отверстия — те, что показаны на чертеже штриховыми '
        + 'линиями.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 80, d: 50, h: 18,
                holes: [{ x: -20, y: 0, r: 4 }, { x: 20, y: 0, r: 4 }],
                label: 'Тело 1' }),
            ...doAction('scene'),
            select('part'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '8'),
            note('отверстия по чертежу', { at: 'scene' }),
        ],
    }),

    result('Последний шаг — проверка. Обойди деталь навигатором и сверь '
        + 'каждый вид с чертежом. Совпало — работа сделана.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 80, d: 50, h: 18,
                holes: [{ x: -20, y: 0, r: 4 }, { x: 20, y: 0, r: 4 }],
                label: 'Тело 1' }),
            select('part'),
            orbit(36),
            note('сверь все три вида', { at: 'scene' }),
        ],
    }),

], { id: 'dr-15-project' });


/* ═══════════════════════════════════════════════════════════════════════
 *  КУРС 3. «ДЕТАЛИ, КОТОРЫЕ СОБИРАЮТСЯ»
 *
 *  Самая практичная тема: здесь ребёнок узнаёт, почему его первая
 *  напечатанная коробка не закрылась. Главное понятие — зазор, и оно
 *  контринтуитивное: чтобы детали собрались, они НЕ должны совпадать
 *  по размеру.
 * ═══════════════════════════════════════════════════════════════════ */

/* ─── УРОК 16. Зазор ─────────────────────────────────────────────── */

export const LESSON_GAP = lesson([

    idea('Сделай вал двадцать миллиметров и отверстие двадцать миллиметров. '
        + 'Кажется, должно подойти? Нет. Не войдёт совсем.', {
        diagram: 'gap',
    }),

    idea('Причина простая: у всего есть погрешность. Принтер кладёт пластик '
        + 'чуть толще, деталь при остывании чуть ведёт. Двадцать на бумаге '
        + 'превращается в двадцать и одну десятую в жизни.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'ровно по размеру — заклинит' },
            { at: 'loose', text: 'с запасом — соберётся' },
            { at: 'note', text: 'реальный размер всегда немного другой' },
        ],
    }),

    act('Поэтому отверстие делают больше вала. Строим пластину и сверлим '
        + 'отверстие не двадцать, а двадцать и четыре десятых.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 60, d: 40, h: 10,
                holes: [{ x: 0, y: 0, r: 10.2 }], label: 'Тело 1' }),
            ...doAction('scene'),
            select('plate'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '20.4'),
            note('запас четыре десятых миллиметра', { at: 'scene' }),
        ],
    }),

    idea('Эти четыре десятых и есть зазор. Для печатных деталей обычно берут '
        + 'от двух до четырёх десятых миллиметра — этого хватает, чтобы '
        + 'собралось, и деталь при этом не болтается.', {
        diagram: 'fits',
        labels: [{ at: 'note', text: 'для печати: от 0,2 до 0,4 мм' }],
    }),

], { id: 'as-16-gap' });


/* ─── УРОК 17. Посадки ───────────────────────────────────────────── */

export const LESSON_FITS = lesson([

    idea('Зазор бывает разный, и от него зависит, как деталь себя ведёт. '
        + 'Инженеры называют это посадкой.', {
        diagram: 'fits',
    }),

    idea('Свободная посадка — зазор побольше. Деталь легко надевается и '
        + 'снимается: так делают крышки, колёса на оси, съёмные части.', {
        diagram: 'fits',
        labels: [
            { at: 'free', text: 'свободная' },
            { at: 'note', text: 'легко снять — крышка, колесо' },
        ],
    }),

    idea('Плотная посадка — зазор маленький. Деталь надевается с усилием и '
        + 'не болтается. Так сажают ручки и втулки.', {
        diagram: 'fits',
        labels: [
            { at: 'tight', text: 'плотная' },
            { at: 'note', text: 'держится крепко, но снять можно' },
        ],
    }),

    idea('Посадка с натягом — зазора нет вовсе, деталь чуть больше отверстия. '
        + 'Её забивают, и обратно она уже не снимается. Так ставят '
        + 'подшипники.', {
        diagram: 'fits',
        labels: [
            { at: 'press', text: 'с натягом' },
            { at: 'note', text: 'поставил один раз и навсегда' },
        ],
    }),

    act('Выбор посадки — это просто выбор числа в поле диаметра. Всё '
        + 'остальное делает тот же инструмент «Отверстие».', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 70, d: 50, h: 12,
                holes: [{ x: -20, y: 0, r: 5.2 }, { x: 0, y: 0, r: 5.05 },
                    { x: 20, y: 0, r: 5 }], label: 'Тело 1' }),
            ...doAction('scene'),
            select('plate'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '20.4'),
            note('свободная посадка', { at: 'scene' }),
        ],
    }),

], { id: 'as-17-fits' });


/* ─── УРОК 18. Коробка с крышкой ─────────────────────────────────── */

export const LESSON_LID = lesson([

    idea('Первая настоящая сборка: коробка и крышка. Крышка должна сниматься, '
        + 'но не болтаться — значит, нужна свободная посадка с небольшим '
        + 'зазором.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'без зазора крышка не наденется' },
            { at: 'loose', text: 'с зазором 0,3 мм — в самый раз' },
        ],
    }),

    act('Сначала коробка: брусок и оболочка с толщиной стенки два '
        + 'миллиметра.', {
        layout: 'full',
        steps: [
            spawn('box', { w: 70, d: 50, h: 30, label: 'Тело 1' }),
            ...doAction('scene'),
            select('box'),
            ...doAction('tool-shell'),
            setProp('Толщина', '2'),
            note('коробка готова', { at: 'scene' }),
        ],
    }),

    act('Теперь крышка. Её внутренний размер должен быть чуть больше '
        + 'наружного размера коробки — на те самые три десятых.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '60.3'),
            setProp('Ширина', '40.3'),
            note('размер коробки плюс зазор', { at: 'scene' }),
        ],
    }),

    act('Выдавливаем крышку и делаем ей оболочку — получится колпачок, '
        + 'который надевается сверху.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 60, d: 40, h: 0.6, label: 'Эскиз 2' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '12'),
            spawn('lid', { w: 60, d: 40, h: 12, y: -70, shell: 2, label: 'Тело 2' }),
            note('крышка готова', { at: 'scene' }),
        ],
    }),

    result('Две детали, которые собираются друг с другом. Если крышка не '
        + 'наделась — увеличь зазор. Болтается — уменьши.', {
        layout: 'full',
        steps: [
            spawn('box', { w: 60, d: 40, h: 30, shell: 2, label: 'Тело 1' }),
            spawn('lid', { w: 60, d: 40, h: 12, y: -78, shell: 2, label: 'Тело 2' }),
            select('lid'),
            orbit(30),
            note('зазор подбирают на практике', { at: 'scene' }),
        ],
    }),

], { id: 'as-18-lid' });


/* ─── УРОК 19. Отверстия под винты ───────────────────────────────── */

export const LESSON_SCREWS = lesson([

    idea('Винт М3 — это винт диаметром три миллиметра. Отверстие под него '
        + 'сверлят три и три десятых: винт должен проходить свободно, а не '
        + 'вкручиваться в пластик.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'ровно 3 мм — винт не пройдёт' },
            { at: 'loose', text: '3,3 мм — проходит свободно' },
            { at: 'note', text: 'винт М3 — отверстие 3,3' },
        ],
    }),

    idea('Для М4 сверлят четыре и три. Правило простое: диаметр винта плюс '
        + 'три десятых. Запомни эти два размера — они нужны постоянно.', {
        diagram: 'fits',
        labels: [{ at: 'note', text: 'М3 — 3,3 мм, М4 — 4,3 мм' }],
    }),

    act('Делаем пластину с отверстиями под М3.', {
        layout: 'full',
        steps: [
            spawn('plate', { w: 80, d: 40, h: 6,
                holes: [{ x: -28, y: 0, r: 1.65 }, { x: -10, y: 0, r: 1.65 },
                    { x: 10, y: 0, r: 2.15 }, { x: 28, y: 0, r: 2.15 }],
                label: 'Тело 1' }),
            ...doAction('scene'),
            select('plate'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '3.3'),
            note('отверстие под М3', { at: 'scene' }),
        ],
    }),

    idea('Ещё бывает потай — когда шляпка винта должна утонуть заподлицо. '
        + 'Тогда сверху отверстие расширяют конусом, и винт садится вровень '
        + 'с поверхностью.', {
        diagram: 'section',
        labels: [
            { at: 'whole', text: 'обычное отверстие' },
            { at: 'cut', text: 'потай: шляпка утоплена' },
            { at: 'note', text: 'потай нужен там, где ничего не должно торчать' },
        ],
    }),

], { id: 'as-19-screws' });


/* ─── УРОК 20. Пазы ──────────────────────────────────────────────── */

export const LESSON_SLOTS = lesson([

    idea('Паз — это вытянутое отверстие. Он нужен, когда деталь должна не '
        + 'просто держаться, а ещё и сдвигаться: подстроить положение, '
        + 'выбрать натяжение.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'круглое отверстие — жёстко' },
            { at: 'loose', text: 'паз — можно двигать' },
            { at: 'note', text: 'паз даёт запас на регулировку' },
        ],
    }),

    act('Паз рисуют в эскизе инструментом «Паз»: задают длину и ширину. '
        + 'Ширина — как у обычного отверстия под винт.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-circle'),
            setProp('Ширина', '3.3'),
            setProp('Длина', '20'),
            note('паз под винт М3', { at: 'scene' }),
        ],
    }),

    act('Закрываем эскиз и вырезаем паз насквозь.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 90, d: 40, h: 0.6, label: 'Эскиз 1' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Операция', 'вырез'),
            spawn('plate', { w: 90, d: 40, h: 8,
                slot: { x: 0, y: 0, len: 25, w: 3.3 }, label: 'Тело 1' }),
            note('деталь можно двигать вдоль паза', { at: 'scene' }),
        ],
    }),

    idea('Правило: паз делают чуть шире винта и настолько длинным, насколько '
        + 'нужен ход. Слишком длинный паз ослабляет деталь.', {
        diagram: 'printable',
        labels: [
            { at: 'thin', text: 'мало материала вокруг паза' },
            { at: 'thinFix', text: 'оставь стенки потолще' },
            { at: 'note', text: 'паз не должен резать деталь пополам' },
        ],
    }),

], { id: 'as-20-slots' });


/* ─── УРОК 21. Защёлка ───────────────────────────────────────────── */

export const LESSON_SNAP = lesson([

    idea('Защёлка — соединение без винтов. Работает потому, что пластик '
        + 'пружинит: язычок отгибается, проскакивает выступ и возвращается '
        + 'на место.', {
        diagram: 'snap',
    }),

    idea('Три стадии: подошла, отогнулась, защёлкнулась. Главное условие — '
        + 'язычок должен быть достаточно длинным и тонким, чтобы согнуться '
        + 'и не сломаться.', {
        diagram: 'snap',
        labels: [
            { at: 'a', text: 'подходит' },
            { at: 'b', text: 'гнётся' },
            { at: 'c', text: 'держит' },
            { at: 'note', text: 'короткий и толстый язычок просто треснет' },
        ],
    }),

    act('Язычок делают вырезом: два узких паза по бокам оставляют посередине '
        + 'полоску, которая и пружинит.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 60, d: 40, h: 4,
                slot: { x: 0, y: -8, len: 30, w: 1.5 }, label: 'Тело 1' }),
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Ширина', '1.5'),
            note('прорези по бокам язычка', { at: 'scene' }),
        ],
    }),

    idea('Крючок на конце делают со скосом с одной стороны: по скосу язычок '
        + 'легко заходит, а прямая сторона не даёт вылезти обратно.', {
        diagram: 'snap',
        labels: [
            { at: 'note', text: 'скос — чтобы вошло, прямая грань — чтобы держало' },
        ],
    }),

], { id: 'as-21-snap' });


/* ─── УРОК 22. Стойки и рёбра жёсткости ──────────────────────────── */

export const LESSON_RIBS = lesson([

    idea('Плоская стенка гнётся. Чтобы деталь держала форму, к ней добавляют '
        + 'рёбра — тонкие перегородки, поставленные на ребро.', {
        diagram: 'printable',
        labels: [
            { at: 'thin', text: 'стенка без рёбер — гнётся' },
            { at: 'thinFix', text: 'ребро держит форму' },
            { at: 'note', text: 'ребро работает как балка' },
        ],
    }),

    act('Ребро — это тот же эскиз и выдавливание, просто узкое и высокое.', {
        layout: 'full',
        steps: [
            spawn('base', { w: 80, d: 60, h: 4, label: 'Тело 1' }),
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Ширина', '2'),
            setProp('Высота', '12'),
            note('ребро жёсткости', { at: 'scene' }),
        ],
    }),

    idea('Стойка — столбик с отверстием под винт. На стойках держат плату '
        + 'внутри корпуса: она не касается дна, и снизу остаётся место '
        + 'для проводов.', {
        diagram: 'section',
        labels: [
            { at: 'whole', text: 'плата лежит на дне' },
            { at: 'cut', text: 'плата на стойках' },
            { at: 'note', text: 'стойка поднимает плату над дном' },
        ],
    }),

    act('Стойка строится так же: круглый эскиз, выдавить вверх, потом '
        + 'отверстие под винт по центру.', {
        layout: 'full',
        steps: [
            spawn('base', { w: 80, d: 60, h: 4, label: 'Тело 1' }),
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-circle'),
            setProp('Диаметр', '8'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '6'),
            spawn('post', { w: 80, d: 60, h: 4, posts: [{ x: -30, y: -20, r: 4, h: 6 }, { x: 30, y: -20, r: 4, h: 6 }, { x: -30, y: 20, r: 4, h: 6 }, { x: 30, y: 20, r: 4, h: 6 }], label: 'Стойки' }),
            note('стойка под плату', { at: 'scene' }),
        ],
    }),

], { id: 'as-22-ribs' });


/* ─── УРОК 23. Корпус для электроники ────────────────────────────── */

export const LESSON_CASE = lesson([

    idea('Собираем всё вместе: корпус для платы. В нём есть коробка, стойки '
        + 'под плату, окно под дисплей и крышка с зазором.', {
        diagram: 'section',
        labels: [
            { at: 'whole', text: 'снаружи просто коробка' },
            { at: 'cut', text: 'внутри стойки и плата' },
            { at: 'note', text: 'корпус проектируют вокруг платы' },
        ],
    }),

    idea('Порядок правильный такой: сначала измерь плату, потом добавь по три '
        + 'миллиметра с каждой стороны на стенки и провода — и только потом '
        + 'строй коробку.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'корпус впритык — плата не влезет' },
            { at: 'loose', text: 'с запасом — соберётся' },
            { at: 'note', text: 'сначала плата, потом корпус' },
        ],
    }),

    act('Строим коробку по размерам платы с запасом и делаем оболочку.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '76'),
            setProp('Ширина', '56'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '24'),
            spawn('case', { w: 76, d: 56, h: 24, shell: 2, label: 'Корпус' }),
            note('коробка по размерам платы', { at: 'scene' }),
        ],
    }),

    act('Окно под дисплей — обычный вырез в верхней грани, по размерам '
        + 'экрана плюс небольшой зазор.', {
        layout: 'full',
        steps: [
            spawn('case', { w: 76, d: 56, h: 24, shell: 2,
                windowCut: { w: 30, d: 14 }, label: 'Корпус' }),
            ...doAction('scene'),
            select('case'),
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '30.4'),
            setProp('Ширина', '14.4'),
            note('окно под дисплей', { at: 'scene' }),
        ],
    }),

    result('Корпус готов: коробка, стойки, окно. Осталось добавить крышку — '
        + 'ту самую, с зазором из восемнадцатого урока.', {
        layout: 'full',
        steps: [
            spawn('case', { w: 76, d: 56, h: 24, shell: 2,
                windowCut: { w: 30, d: 14 },
                posts: [{ x: -28, y: -18, r: 4, h: 6 }, { x: 28, y: -18, r: 4, h: 6 },
                    { x: -28, y: 18, r: 4, h: 6 }, { x: 28, y: 18, r: 4, h: 6 }],
                label: 'Корпус' }),
            select('case'),
            orbit(32),
            note('всё, что учили, в одной детали', { at: 'scene' }),
        ],
    }),

], { id: 'as-23-case' });


/* ─── УРОК 24. Проверка перед печатью ────────────────────────────── */

export const LESSON_PRINT_CHECK = lesson([

    idea('Перед печатью деталь надо проверить. Принтер кладёт пластик снизу '
        + 'вверх слой за слоем, и не всё, что рисуется на экране, можно '
        + 'напечатать.', {
        diagram: 'printable',
    }),

    idea('Первое — толщина стенок. Тоньше полутора миллиметров печатать '
        + 'нельзя: стенка получится хрупкой и сломается в руках.', {
        diagram: 'printable',
        labels: [
            { at: 'thin', text: 'слишком тонко' },
            { at: 'thinFix', text: 'от 1,5 мм' },
            { at: 'note', text: 'проверь все стенки, не только внешние' },
        ],
    }),

    idea('Второе — нависания. Если часть детали висит в воздухе, класть '
        + 'пластик не на что. Спасают либо поддержки, либо скос: наклонная '
        + 'стенка печатается сама.', {
        diagram: 'printable',
        labels: [
            { at: 'over', text: 'висит в воздухе' },
            { at: 'overFix', text: 'скос печатается сам' },
            { at: 'note', text: 'наклон до 45 градусов держится без поддержек' },
        ],
    }),

    act('В редакторе толщину стенки видно через оболочку, а нависания — '
        + 'если посмотреть на деталь сбоку навигатором.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 50, d: 50, h: 30, shell: 2, label: 'Тело 1' }),
            ...doAction('viewcube'),
            orbit(0),
            note('смотри сбоку — так видно нависания', { at: 'scene' }),
        ],
    }),

    result('Проверил толщины, проверил нависания — можно печатать. Эта '
        + 'привычка экономит часы: неудачная печать идёт в мусор.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 50, d: 50, h: 30, shell: 2, label: 'Тело 1' }),
            select('part'),
            orbit(34),
            note('готово к печати', { at: 'scene' }),
        ],
    }),

], { id: 'as-24-print' });


/* ═══════════════════════════════════════════════════════════════════════
 *  КУРС 4. «ТЕЛА ВРАЩЕНИЯ И МАССИВЫ»
 *
 *  Формы, которые не сделать выдавливанием. Здесь появляются две новые
 *  операции — вращение профиля вокруг оси и размножение по кругу, — и
 *  ими делаются вещи, которые крутятся: воронка, шестерёнка, пружина.
 * ═══════════════════════════════════════════════════════════════════ */

/* ─── УРОК 25. Вращение профиля ──────────────────────────────────── */

export const LESSON_REVOLVE = lesson([

    idea('Выдавливание тянет фигуру прямо. Но чашку или воронку так не '
        + 'сделать — у них круглое сечение, которое меняется по высоте.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'выдавливание — только прямо' },
            { at: 'solid', text: 'нужна другая операция' },
        ],
    }),

    idea('Для таких форм есть вращение. Рисуют половину профиля рядом с осью, '
        + 'и он обходит вокруг неё полный круг — получается тело вращения.', {
        diagram: 'planes',
        labels: [
            { at: 'xz', text: 'профиль рисуют сбоку' },
            { at: 'note', text: 'ось — линия, вокруг которой всё крутится' },
        ],
    }),

    act('Рисуем профиль в эскизе: линия оси и контур будущей детали рядом '
        + 'с ней.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-line'),
            spawn('sk', { w: 40, d: 30, h: 0.6, label: 'Профиль' }),
            note('половина сечения детали', { at: 'scene' }),
        ],
    }),

    act('Закрываем эскиз, нажимаем «Вращение» и задаём триста шестьдесят '
        + 'градусов — профиль обходит ось и превращается в тело.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 40, d: 30, h: 0.6, label: 'Профиль' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-revolve'),
            setProp('Угол', '360'),
            spawn('cup', { shape: 'cone', w: 60, h: 50, label: 'Тело 1' }),
            note('получилось тело вращения', { at: 'scene' }),
        ],
    }),

    idea('Главное правило: профиль не должен пересекать ось. Если он её '
        + 'перечеркнёт, деталь вывернется сама в себя и операция не '
        + 'выполнится.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'профиль пересёк ось — ошибка' },
            { at: 'loose', text: 'профиль сбоку от оси — верно' },
            { at: 'note', text: 'ось трогать нельзя' },
        ],
    }),

], { id: 'rv-25-revolve' });


/* ─── УРОК 26. Воронка ───────────────────────────────────────────── */

export const LESSON_FUNNEL = lesson([

    idea('Воронка — классическая деталь из методички. В ней сразу всё: '
        + 'широкий конус, узкий носик и скругление на стыке, чтобы вода '
        + 'не застревала.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'профиль воронки' },
            { at: 'solid', text: 'конус плюс носик' },
        ],
    }),

    act('Профиль воронки — ломаная линия: наклонная стенка конуса, потом '
        + 'вертикальный носик.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-line'),
            spawn('sk', { w: 50, d: 40, h: 0.6, label: 'Профиль' }),
            note('конус и носик одной ломаной', { at: 'scene' }),
        ],
    }),

    act('Вращаем профиль — получается воронка.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 50, d: 40, h: 0.6, label: 'Профиль' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-revolve'),
            setProp('Угол', '360'),
            spawn('funnel', { shape: 'cone', w: 64, h: 54, label: 'Воронка' }),
            note('воронка готова', { at: 'scene' }),
        ],
    }),

    act('Осталось скругление на стыке конуса и носика: там собирается '
        + 'жидкость, и острый угол мешает ей стекать.', {
        layout: 'full',
        steps: [
            spawn('funnel', { shape: 'cone', w: 64, h: 54, label: 'Воронка' }),
            ...doAction('scene'),
            select('funnel'),
            ...doAction('tool-fillet'),
            setProp('Радиус', '3'),
            note('скругление на переходе', { at: 'scene' }),
        ],
    }),

    result('Воронка готова. Проверь толщину стенки — она должна быть '
        + 'не меньше полутора миллиметров, иначе не напечатается.', {
        layout: 'full',
        steps: [
            spawn('funnel', { shape: 'cone', w: 64, h: 54, label: 'Воронка' }),
            select('funnel'),
            orbit(34),
            note('можно печатать', { at: 'scene' }),
        ],
    }),

], { id: 'rv-26-funnel' });


/* ─── УРОК 27. Круговой массив ───────────────────────────────────── */

export const LESSON_PATTERN = lesson([

    idea('Представь: в диске нужно двадцать отверстий по кругу. Рисовать '
        + 'каждое — двадцать раз считать координаты и всё равно ошибиться.', {
        diagram: 'dims',
        labels: [{ at: 'note', text: 'двадцать отверстий вручную — час работы' }],
    }),

    idea('Круговой массив делает это сам: строишь ОДИН элемент, указываешь '
        + 'ось и число копий — редактор расставляет их по окружности '
        + 'с равным шагом.', {
        diagram: 'fits',
        labels: [
            { at: 'free', text: 'один элемент' },
            { at: 'tight', text: 'массив' },
            { at: 'press', text: 'двадцать копий' },
            { at: 'note', text: 'считает редактор, а не ты' },
        ],
    }),

    act('Строим диск и делаем в нём одно отверстие — образец для массива.', {
        layout: 'full',
        steps: [
            spawn('disc', { shape: 'cylinder', w: 60, h: 8, label: 'Диск' }),
            ...doAction('scene'),
            select('disc'),
            ...doAction('tool-hole'),
            setProp('Диаметр', '5'),
            note('один элемент — образец', { at: 'scene' }),
        ],
    }),

    idea('Дальше указываешь ось вращения и число копий. Главная выгода: '
        + 'поменяешь образец — изменятся все копии сразу.', {
        diagram: 'fits',
        labels: [{ at: 'note', text: 'правишь один — меняются все' }],
    }),

], { id: 'rv-27-pattern' });


/* ─── УРОК 28. Шестерёнка ────────────────────────────────────────── */

export const LESSON_GEAR = lesson([

    idea('Шестерёнка — круговой массив в чистом виде. Один зуб, размноженный '
        + 'по кругу, и отверстие под вал в центре.', {
        diagram: 'fits',
        labels: [
            { at: 'free', text: 'зуб' },
            { at: 'tight', text: 'массив' },
            { at: 'press', text: 'шестерёнка' },
            { at: 'note', text: 'зубья всегда одинаковые' },
        ],
    }),

    act('Сначала диск нужного диаметра — это заготовка шестерёнки.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-circle'),
            setProp('Диаметр', '60'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '10'),
            spawn('gear', { shape: 'cylinder', w: 60, h: 10, label: 'Заготовка' }),
            note('диск готов', { at: 'scene' }),
        ],
    }),

    act('Теперь один зуб — и круговой массив на двенадцать копий. '
        + 'Двенадцать зубьев появляются разом.', {
        layout: 'full',
        steps: [
            spawn('gear', { shape: 'gear', w: 60, h: 10, teeth: 12,
                label: 'Шестерёнка' }),
            ...doAction('scene'),
            select('gear'),
            setProp('Копий', '12'),
            note('двенадцать зубьев', { at: 'scene' }),
        ],
    }),

    idea('Число зубьев — не любое. Оно связано с диаметром через модуль: '
        + 'диаметр делить на число зубьев. У сцепляющихся шестерён модуль '
        + 'должен совпадать, иначе зубья не войдут друг в друга.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'разный модуль — не зацепятся' },
            { at: 'loose', text: 'одинаковый — работают' },
            { at: 'note', text: 'модуль = диаметр делить на число зубьев' },
        ],
    }),

    result('Осталось отверстие под вал в центре — и шестерёнку можно '
        + 'ставить в механизм.', {
        layout: 'full',
        steps: [
            spawn('gear', { shape: 'gear', w: 60, h: 10, teeth: 12,
                holes: [{ x: 0, y: 0, r: 5 }], label: 'Шестерёнка' }),
            select('gear'),
            orbit(30),
            note('готова к сборке', { at: 'scene' }),
        ],
    }),

], { id: 'rv-28-gear' });


/* ─── УРОК 29. Пара шестерёнок ───────────────────────────────────── */

export const LESSON_GEAR_PAIR = lesson([

    idea('Одна шестерёнка ничего не делает. Работают они парами: одна '
        + 'ведущая, вторая ведомая. Зубья входят друг в друга и передают '
        + 'вращение.', {
        diagram: 'fits',
        labels: [
            { at: 'free', text: 'ведущая' },
            { at: 'tight', text: 'зацепление' },
            { at: 'press', text: 'ведомая' },
            { at: 'note', text: 'вторая крутится в обратную сторону' },
        ],
    }),

    idea('Если у первой двенадцать зубьев, а у второй двадцать четыре, '
        + 'то вторая крутится вдвое медленнее — зато вдвое сильнее. '
        + 'Это и есть передаточное отношение.', {
        diagram: 'dims',
        labels: [
            { at: 'w', text: '12' },
            { at: 'd', text: '24' },
            { at: 'note', text: 'больше зубьев — медленнее и сильнее' },
        ],
    }),

    act('Строим вторую шестерёнку — с числом зубьев вдвое больше.', {
        layout: 'full',
        steps: [
            spawn('g1', { shape: 'gear', w: 50, h: 10, teeth: 12,
                holes: [{ x: 0, y: 0, r: 4 }], label: 'Ведущая' }),
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-circle'),
            setProp('Диаметр', '100'),
            note('вторая вдвое больше', { at: 'scene' }),
        ],
    }),

    act('Ставим их рядом так, чтобы зубья вошли в зацепление. Расстояние '
        + 'между центрами — сумма радиусов.', {
        layout: 'full',
        steps: [
            spawn('g1', { shape: 'gear', w: 46, h: 10, teeth: 12,
                holes: [{ x: 0, y: 0, r: 4 }], x: -56, label: 'Ведущая' }),
            spawn('g2', { shape: 'gear', w: 72, h: 10, teeth: 24,
                holes: [{ x: 0, y: 0, r: 5 }], x: 42, label: 'Ведомая' }),
            select('g2'),
            note('зубья вошли в зацепление', { at: 'scene' }),
        ],
    }),

], { id: 'rv-29-gear-pair' });


/* ─── УРОК 30. Спираль ───────────────────────────────────────────── */

export const LESSON_COIL = lesson([

    idea('Пружина — тоже тело вращения, но особое: профиль не просто '
        + 'обходит ось, а поднимается по ней. Такая линия называется '
        + 'спиралью.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'кружок проволоки' },
            { at: 'solid', text: 'идёт по спирали вверх' },
        ],
    }),

    idea('У спирали три числа: шаг — на сколько поднимается за один оборот, '
        + 'число витков и толщина проволоки. Из них и получается пружина.', {
        diagram: 'dims',
        labels: [
            { at: 'w', text: 'шаг' },
            { at: 'h', text: 'витки' },
            { at: 'd', text: 'проволока' },
            { at: 'note', text: 'три числа задают пружину целиком' },
        ],
    }),

    act('В редакторе есть готовый инструмент «Спираль»: задаёшь диаметр, '
        + 'шаг, число витков и толщину проволоки.', {
        layout: 'full',
        steps: [
            ...doAction('tool-revolve'),
            setProp('Шаг', '8'),
            setProp('Витков', '6'),
            spawn('spring', { shape: 'spring', w: 36, h: 56, coils: 6, wire: 3,
                label: 'Пружина' }),
            note('пружина построена', { at: 'scene' }),
        ],
    }),

    idea('Чем тоньше проволока и больше витков, тем мягче пружина. Толстая '
        + 'проволока и мало витков — жёсткая. Это правило пригодится '
        + 'в следующем уроке.', {
        diagram: 'fits',
        labels: [
            { at: 'free', text: 'тонкая — мягкая' },
            { at: 'press', text: 'толстая — жёсткая' },
            { at: 'note', text: 'жёсткость зависит от толщины проволоки' },
        ],
    }),

], { id: 'rv-30-coil' });


/* ─── УРОК 31. Амортизатор ───────────────────────────────────────── */

export const LESSON_DAMPER = lesson([

    idea('Амортизатор — пружина между двумя опорами. Пружина сжимается '
        + 'и гасит удар, опоры держат её на месте.', {
        diagram: 'section',
        labels: [
            { at: 'whole', text: 'снаружи цилиндр' },
            { at: 'cut', text: 'внутри пружина' },
            { at: 'note', text: 'опоры не дают пружине уехать вбок' },
        ],
    }),

    act('Сначала нижняя опора — диск с бортиком, в который сядет пружина.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-circle'),
            setProp('Диаметр', '40'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '6'),
            spawn('base', { shape: 'cylinder', w: 40, h: 8, label: 'Опора' }),
            note('нижняя опора', { at: 'scene' }),
        ],
    }),

    act('Сверху ставим пружину — ту самую, из прошлого урока.', {
        layout: 'full',
        steps: [
            spawn('base', { shape: 'cylinder', w: 40, h: 8, y: 34,
                label: 'Опора' }),
            spawn('spring', { shape: 'spring', w: 32, h: 50, coils: 6, wire: 3,
                y: -10, label: 'Пружина' }),
            select('spring'),
            note('пружина села в опору', { at: 'scene' }),
        ],
    }),

    result('Амортизатор собран. Диаметр пружины должен быть чуть меньше '
        + 'бортика опоры — тот самый зазор из третьего курса.', {
        layout: 'full',
        steps: [
            spawn('base', { shape: 'cylinder', w: 40, h: 8, y: 34,
                label: 'Опора' }),
            spawn('spring', { shape: 'spring', w: 32, h: 50, coils: 6, wire: 3,
                y: -10, label: 'Пружина' }),
            orbit(28),
            note('зазор нужен и здесь', { at: 'scene' }),
        ],
    }),

], { id: 'rv-31-damper' });


/* ─── УРОК 32. Резьба ────────────────────────────────────────────── */

export const LESSON_THREAD = lesson([

    idea('Резьба — это спираль, только не пружина, а канавка на цилиндре. '
        + 'По ней винт вкручивается в гайку.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'канавка по спирали' },
            { at: 'solid', text: 'винт вкручивается' },
        ],
    }),

    idea('У резьбы тоже есть шаг — на сколько винт продвинется за один '
        + 'оборот. У М3 шаг полмиллиметра: десять оборотов — пять '
        + 'миллиметров хода.', {
        diagram: 'dims',
        labels: [
            { at: 'w', text: 'М3' },
            { at: 'h', text: 'шаг 0,5' },
            { at: 'note', text: 'обозначение резьбы: буква М и диаметр' },
        ],
    }),

    act('В редакторе резьбу делают инструментом «Резьба» на готовом '
        + 'цилиндре: выбираешь боковую поверхность и задаёшь размер.', {
        layout: 'full',
        steps: [
            spawn('bolt', { shape: 'shaft', w: 30, h: 50, label: 'Винт' }),
            ...doAction('scene'),
            select('bolt'),
            ...doAction('tool-revolve'),
            setProp('Резьба', 'М6'),
            note('резьба нарезана', { at: 'scene' }),
        ],
    }),

    idea('Печатать резьбу на 3D-принтере трудно: мелкие витки смазываются. '
        + 'Поэтому в печатных деталях чаще ставят обычный винт с гайкой, '
        + 'а резьбу оставляют металлу.', {
        diagram: 'printable',
        labels: [
            { at: 'thin', text: 'мелкая резьба не пропечатается' },
            { at: 'thinFix', text: 'ставь винт с гайкой' },
            { at: 'note', text: 'резьба крупнее М8 печатается сносно' },
        ],
    }),

], { id: 'rv-32-thread' });


/* ─── УРОК 33. Протяжка по траектории ────────────────────────────── */

export const LESSON_SWEEP = lesson([

    idea('Есть третий способ получить тело: провести сечение вдоль линии. '
        + 'Так делают ручки, скобы, трубы — всё, что тянется по кривой.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'сечение' },
            { at: 'solid', text: 'едет вдоль траектории' },
        ],
    }),

    idea('Нужны два эскиза: сечение — что тянем, и траектория — куда. '
        + 'Сечение обычно круг или прямоугольник, траектория — ломаная '
        + 'или дуга.', {
        diagram: 'planes',
        labels: [
            { at: 'xy', text: 'сечение здесь' },
            { at: 'xz', text: 'траектория здесь' },
            { at: 'note', text: 'два эскиза на разных плоскостях' },
        ],
    }),

    act('Рисуем траекторию — линию, по которой пойдёт ручка.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('planes'),
            ...doAction('plane-xz'),
            openPanel('sketch-mode'),
            ...doAction('sk-line'),
            spawn('path', { w: 60, d: 40, h: 0.6, label: 'Траектория' }),
            note('путь для сечения', { at: 'scene' }),
        ],
    }),

    act('Потом сечение — круг на другой плоскости — и запускаем протяжку.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            ...doAction('sk-circle'),
            setProp('Диаметр', '8'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-revolve'),
            spawn('handle', { shape: 'shaft', w: 30, h: 56,
                profile: [[0.5, 0.34], [0.5, 0.32], [0.5, 0.34]],
                label: 'Ручка' }),
            note('сечение прошло по траектории', { at: 'scene' }),
        ],
    }),

], { id: 'rv-33-sweep' });


/* ─── УРОК 34. Проект: механизм из двух деталей ──────────────────── */

export const LESSON_MECHANISM = lesson([

    idea('Итог курса: собрать механизм, в котором что-то крутится. '
        + 'Две шестерёнки на валах — самое понятное.', {
        diagram: 'fits',
        labels: [
            { at: 'free', text: 'вал' },
            { at: 'tight', text: 'шестерёнка' },
            { at: 'press', text: 'вторая' },
            { at: 'note', text: 'детали должны собираться с зазором' },
        ],
    }),

    idea('Здесь сходятся все три курса: тела вращения из четвёртого, '
        + 'зазоры из третьего, чертёж из второго. Шестерёнка садится '
        + 'на вал с плотной посадкой, чтобы не проворачивалась.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'без зазора не наденется' },
            { at: 'loose', text: 'с зазором 0,1 — плотно' },
            { at: 'note', text: 'плотная посадка: шестерёнка не проворачивается' },
        ],
    }),

    act('Строим вал — ступенчатый цилиндр под подшипник и шестерёнку.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-line'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-revolve'),
            setProp('Угол', '360'),
            spawn('shaft', { shape: 'shaft', w: 34, h: 60, label: 'Вал' }),
            note('вал готов', { at: 'scene' }),
        ],
    }),

    act('Насаживаем на него шестерёнку — отверстие в ней на десятую '
        + 'миллиметра больше вала.', {
        layout: 'full',
        steps: [
            spawn('shaft', { shape: 'shaft', w: 34, h: 60, x: -40,
                label: 'Вал' }),
            spawn('gear', { shape: 'gear', w: 56, h: 10, teeth: 16,
                holes: [{ x: 0, y: 0, r: 5 }], x: 34, label: 'Шестерёнка' }),
            select('gear'),
            note('плотная посадка на вал', { at: 'scene' }),
        ],
    }),

    result('Механизм собран. Проверь: шестерёнки одного модуля, зазоры '
        + 'выдержаны, всё печатается без нависаний.', {
        layout: 'full',
        steps: [
            spawn('shaft', { shape: 'shaft', w: 34, h: 60, x: -40,
                label: 'Вал' }),
            spawn('gear', { shape: 'gear', w: 56, h: 10, teeth: 16,
                holes: [{ x: 0, y: 0, r: 5 }], x: 34, label: 'Шестерёнка' }),
            orbit(32),
            note('курс пройден', { at: 'scene' }),
        ],
    }),

], { id: 'rv-34-mechanism' });


/* ═══════════════════════════════════════════════════════════════════════
 *  КУРС 5. «ПРОЕКТИРОВАНИЕ ПО ЗАДАНИЮ»
 *
 *  Последний курс, и он другой: здесь не показывают, куда нажать, —
 *  здесь учат думать. Готового решения нет, есть требования и
 *  ограничения. Поэтому кадров-объяснений больше, чем кадров-действий:
 *  инструменты ребёнок уже знает, ему нужен способ рассуждать.
 * ═══════════════════════════════════════════════════════════════════ */

/* ─── УРОК 35. Техническое задание ───────────────────────────────── */

export const LESSON_BRIEF = lesson([

    idea('Настоящая работа инженера начинается не с эскиза, а с требований. '
        + 'Их называют техническим заданием.', {
        diagram: 'brief',
    }),

    idea('Главное правило: требование — это число, которое можно проверить '
        + 'линейкой. «Должно быть удобно» — пожелание. «Ручка диаметром '
        + 'от двадцати пяти до тридцати» — требование.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'удобно держать' },
            { at: 'a2', text: 'ручка 25–30 мм' },
            { at: 'note', text: 'сначала переведи все пожелания в числа' },
        ],
    }),

    idea('Требования бывают разные: габариты, вес, что деталь должна '
        + 'выдерживать, из чего печатается. Прежде чем строить, выпиши '
        + 'их все — потом по ним же себя и проверишь.', {
        diagram: 'dims',
        labels: [
            { at: 'note', text: 'выписал требования — получил критерии проверки' },
        ],
    }),

    act('Проверять себя будешь тем же инструментом, что и раньше: выделяешь '
        + 'деталь и смотришь габариты в свойствах. Требование сказано '
        + 'числом — значит, число можно сверить.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 60, d: 40, h: 20, label: 'Твоя деталь' }),
            ...doAction('scene'),
            select('part'),
            setProp('Габариты', '60 × 40 × 20'),
            note('сверяем с требованием', { at: 'scene' }),
        ],
    }),

    result('С этого урока задания меняются: размеров в тексте больше не '
        + 'будет, будут требования. Как их выполнить — решаешь сам.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 60, d: 40, h: 20, label: 'Твоя деталь' }),
            select('part'),
            orbit(28),
            note('решение может быть любым', { at: 'scene' }),
        ],
    }),

], { id: 'pr-35-brief' });


/* ─── УРОК 36. От идеи к эскизу ──────────────────────────────────── */

export const LESSON_IDEA = lesson([

    idea('Между «придумал» и «построил» есть шаг, который пропускают почти '
        + 'все: набросок на бумаге. Он экономит часы.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'сначала на бумаге' },
            { at: 'solid', text: 'потом в редакторе' },
        ],
    }),

    idea('На наброске проверяют главное: сколько деталей, как они держатся '
        + 'друг за друга, где что торчит. Стереть карандаш проще, чем '
        + 'переделывать модель.', {
        diagram: 'split',
        labels: [
            { at: 'whole', text: 'придумал одной деталью' },
            { at: 'parts', text: 'понял, что нужно две' },
            { at: 'note', text: 'на бумаге это видно за минуту' },
        ],
    }),

    idea('Дальше набросок превращается в размеры. Каждую линию наброска '
        + 'нужно суметь назвать числом — если не получается, значит '
        + 'идея ещё не додумана.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'примерно вот такой' },
            { at: 'a2', text: '60 × 40 × 20 мм' },
            { at: 'note', text: 'не можешь назвать число — думай дальше' },
        ],
    }),

    act('Только теперь открываем редактор — с готовыми числами строить '
        + 'быстро и без переделок.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '60'),
            setProp('Ширина', '40'),
            note('числа уже известны с наброска', { at: 'scene' }),
        ],
    }),

], { id: 'pr-36-idea' });


/* ─── УРОК 37. Держатель для наушников ───────────────────────────── */

export const LESSON_HOLDER = lesson([

    idea('Первое задание по требованиям. Держатель для наушников: должен '
        + 'висеть на краю стола, не царапать провод и не падать, когда '
        + 'наушники висят.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'не должен падать' },
            { at: 'a2', text: 'зацеп по толщине стола' },
            { at: 'b', text: 'не царапать провод' },
            { at: 'b2', text: 'все края скруглены' },
            { at: 'note', text: 'три требования — три числа' },
        ],
    }),

    idea('Первое число берут с реального стола: измерь толщину столешницы '
        + 'линейкой и добавь запас в полмиллиметра, чтобы держатель '
        + 'надевался.', {
        diagram: 'measure',
        labels: [
            { at: 'w', text: '25' },
            { at: 'gap', text: 'запас' },
            { at: 'note', text: 'меряем стол, а не гадаем' },
        ],
    }),

    act('Строим профиль держателя: это буква Г — вертикальная часть '
        + 'и крючок, который цепляется за стол.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-line'),
            spawn('sk', { w: 60, d: 40, h: 0.6, label: 'Профиль' }),
            note('профиль зацепа', { at: 'scene' }),
        ],
    }),

    act('Выдавливаем на ширину держателя и скругляем все края — по '
        + 'второму требованию.', {
        layout: 'full',
        steps: [
            openPanel('sketch-mode'),
            spawn('sk', { w: 60, d: 40, h: 0.6, label: 'Профиль' }),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '30'),
            spawn('holder', { w: 60, d: 30, h: 40, fillet: 4,
                label: 'Держатель' }),
            note('края скруглены', { at: 'scene' }),
        ],
    }),

    result('Проверь по требованиям: зацеп по толщине стола, края скруглены, '
        + 'держится под весом наушников. Все три выполнены — работа сдана.', {
        layout: 'full',
        steps: [
            spawn('holder', { w: 60, d: 30, h: 40, fillet: 4,
                label: 'Держатель' }),
            select('holder'),
            orbit(30),
            note('сверься со списком требований', { at: 'scene' }),
        ],
    }),

], { id: 'pr-37-holder' });


/* ─── УРОК 38. Измерение реального предмета ──────────────────────── */

export const LESSON_MEASURE = lesson([

    idea('Чтобы деталь подошла к реальной вещи, вещь надо измерить. Это '
        + 'звучит очевидно, но именно здесь ошибаются чаще всего.', {
        diagram: 'measure',
    }),

    idea('Меряют по трём направлениям и записывают сразу: длина, ширина, '
        + 'толщина. Меряют в самом широком месте — по нему и делают '
        + 'посадочное место.', {
        diagram: 'measure',
        labels: [
            { at: 'w', text: '72' },
            { at: 'h', text: '148' },
            { at: 'note', text: 'меряй в самом широком месте' },
        ],
    }),

    idea('И обязательно запас. Телефон в чехле толще на пару миллиметров, '
        + 'плата с припаянными проводами — тоже. Забыл запас — деталь '
        + 'придётся печатать заново.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'без запаса — не влезет' },
            { at: 'loose', text: 'с запасом — сядет' },
            { at: 'note', text: 'запас 2 мм спасает от переделки' },
        ],
    }),

    act('Числа с линейки вводим прямо в эскиз — так они не потеряются '
        + 'и не переврутся по дороге.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            ...doAction('sk-dim'),
            setProp('Длина', '74'),
            setProp('Ширина', '10'),
            note('размер предмета плюс запас', { at: 'scene' }),
        ],
    }),

], { id: 'pr-38-measure' });


/* ─── УРОК 39. Органайзер для стола ──────────────────────────────── */

export const LESSON_ORGANIZER = lesson([

    idea('Задание с ограничением: органайзер под свои вещи, но целиком '
        + 'должен влезать в сто пятьдесят миллиметров. Ограничение '
        + 'меняет всё — придётся выбирать.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'вместить всё нужное' },
            { at: 'a2', text: 'уложиться в 150 мм' },
            { at: 'note', text: 'ограничение заставляет выбирать главное' },
        ],
    }),

    idea('Сначала список: что должно поместиться. Карандаши, линейка, '
        + 'ластик. Каждый предмет измеряем — и складываем ширины. '
        + 'Не влезло? Значит, что-то придётся убрать.', {
        diagram: 'measure',
        labels: [{ at: 'note', text: 'сложи ширины — сравни с ограничением' }],
    }),

    act('Строим основание в пределах ограничения и делим его '
        + 'перегородками на отсеки.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '150'),
            setProp('Ширина', '80'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '60'),
            spawn('org', { w: 150, d: 80, h: 60, shell: 3,
                label: 'Органайзер' }),
            note('в пределах 150 мм', { at: 'scene' }),
        ],
    }),

    result('Проверь: влезло ли всё, что планировал, и не вышел ли '
        + 'за габарит. Оба условия — часть задания.', {
        layout: 'full',
        steps: [
            spawn('org', { w: 150, d: 80, h: 60, shell: 3,
                label: 'Органайзер' }),
            select('org'),
            orbit(30),
            note('оба требования выполнены', { at: 'scene' }),
        ],
    }),

], { id: 'pr-39-organizer' });


/* ─── УРОК 40. Разборные конструкции ─────────────────────────────── */

export const LESSON_SPLIT = lesson([

    idea('Иногда деталь не влезает на стол принтера или её нельзя '
        + 'напечатать целиком из-за нависаний. Тогда её делят на части.', {
        diagram: 'split',
    }),

    idea('Делят не где попало, а там, где стык не мешает: по плоскости, '
        + 'на невидимой стороне, вдали от нагрузки.', {
        diagram: 'split',
        labels: [
            { at: 'whole', text: 'слишком большая' },
            { at: 'parts', text: 'стык сзади' },
            { at: 'note', text: 'стык прячут туда, где его не видно' },
        ],
    }),

    idea('Части должны не просто прикладываться, а фиксироваться: шип в паз, '
        + 'штифт в отверстие. И между ними — зазор, как во всех сборках.', {
        diagram: 'gap',
        labels: [
            { at: 'tight', text: 'шип ровно по пазу — не войдёт' },
            { at: 'loose', text: 'с зазором 0,2 — соберётся' },
            { at: 'note', text: 'шип и паз тоже требуют зазора' },
        ],
    }),

    act('Строим две части и на стыке делаем шип с одной стороны и паз '
        + 'с другой.', {
        layout: 'full',
        steps: [
            spawn('p1', { w: 60, d: 50, h: 30, x: -50, label: 'Часть 1' }),
            spawn('p2', { w: 60, d: 50, h: 30, x: 50,
                holes: [{ x: 0, y: 0, r: 6 }], label: 'Часть 2' }),
            select('p2'),
            note('шип и паз на стыке', { at: 'scene' }),
        ],
    }),

], { id: 'pr-40-split' });


/* ─── УРОК 41. Корпус робота ─────────────────────────────────────── */

export const LESSON_ROBOT = lesson([

    idea('Задание из робототехники: корпус под конкретную плату и мотор. '
        + 'Здесь ничего нельзя придумать — всё диктует начинка.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'плата должна встать' },
            { at: 'a2', text: 'стойки под её отверстия' },
            { at: 'b', text: 'мотор не должен болтаться' },
            { at: 'b2', text: 'посадочное по размеру мотора' },
            { at: 'note', text: 'сначала начинка, потом корпус' },
        ],
    }),

    idea('Порядок такой: измерь плату и мотор, расставь их мысленно внутри, '
        + 'добавь по три миллиметра на стенки и провода — и только тогда '
        + 'задавай габариты корпуса.', {
        diagram: 'measure',
        labels: [{ at: 'note', text: 'корпус — последнее, что появляется' }],
    }),

    act('Строим коробку по посчитанным габаритам и делаем оболочку.', {
        layout: 'full',
        steps: [
            ...doAction('tool-sketch'),
            openPanel('sketch-mode'),
            ...doAction('sk-rect'),
            setProp('Длина', '90'),
            setProp('Ширина', '70'),
            ...doAction('sk-finish'),
            openPanel(null),
            ...doAction('tool-extrude'),
            setProp('Высота', '40'),
            spawn('body', { w: 90, d: 70, h: 40, shell: 2, label: 'Корпус' }),
            note('коробка по размерам начинки', { at: 'scene' }),
        ],
    }),

    act('Добавляем стойки под плату — точно под её отверстия — и вырез '
        + 'под вал мотора.', {
        layout: 'full',
        steps: [
            spawn('body', { w: 90, d: 70, h: 40, shell: 2,
                posts: [{ x: -30, y: -22, r: 4, h: 8 },
                    { x: 30, y: -22, r: 4, h: 8 },
                    { x: -30, y: 22, r: 4, h: 8 },
                    { x: 30, y: 22, r: 4, h: 8 }],
                windowCut: { w: 22, d: 16 }, label: 'Корпус' }),
            select('body'),
            note('стойки и вырез под мотор', { at: 'scene' }),
        ],
    }),

    result('Корпус готов. Проверка простая: возьми настоящую плату '
        + 'и приложи — стойки должны совпасть с её отверстиями.', {
        layout: 'full',
        steps: [
            spawn('body', { w: 90, d: 70, h: 40, shell: 2,
                posts: [{ x: -30, y: -22, r: 4, h: 8 },
                    { x: 30, y: -22, r: 4, h: 8 },
                    { x: -30, y: 22, r: 4, h: 8 },
                    { x: 30, y: 22, r: 4, h: 8 }],
                windowCut: { w: 22, d: 16 }, label: 'Корпус' }),
            orbit(32),
            note('проверь на настоящей плате', { at: 'scene' }),
        ],
    }),

], { id: 'pr-41-robot' });


/* ─── УРОК 42. Массогабариты ─────────────────────────────────────── */

export const LESSON_MASS = lesson([

    idea('У детали есть не только размеры, но и вес. Он важнее, чем кажется: '
        + 'тяжёлая деталь дольше печатается, больше стоит и хуже работает '
        + 'в движущемся механизме.', {
        diagram: 'lighten',
    }),

    idea('Вес считается сам: объём умножить на плотность пластика. '
        + 'В редакторе для этого есть инструмент «Масса и объём» — '
        + 'он показывает и то, и другое.', {
        diagram: 'dims',
        labels: [
            { at: 'note', text: 'объём × плотность = вес детали' },
        ],
    }),

    act('Смотрим массу готовой детали: выделяем тело и открываем анализ.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 80, d: 60, h: 20, label: 'Тело 1' }),
            ...doAction('scene'),
            select('part'),
            setProp('Объём', '96 000 мм³'),
            setProp('Масса', '119 г'),
            note('деталь тяжёлая', { at: 'scene' }),
        ],
    }),

    idea('Сравнивай варианты по весу. Одна и та же деталь сплошной и '
        + 'с рёбрами отличается втрое — а держит почти так же.', {
        diagram: 'lighten',
        labels: [
            { at: 'solid', text: 'сплошная' },
            { at: 'ribbed', text: 'с рёбрами' },
            { at: 'note', text: 'втрое легче при той же прочности' },
        ],
    }),

], { id: 'pr-42-mass' });


/* ─── УРОК 43. Оптимизация ───────────────────────────────────────── */

export const LESSON_OPTIMIZE = lesson([

    idea('Убрать материал можно почти везде — вопрос только где именно. '
        + 'Ошибёшься местом, и деталь сломается в первый же день.', {
        diagram: 'lighten',
    }),

    idea('Правило простое: нагрузка идёт по краям, а середина обычно '
        + 'работает мало. Поэтому убирают середину, оставляя рёбра '
        + 'по контуру и крест-накрест.', {
        diagram: 'lighten',
        labels: [
            { at: 'solid', text: 'материал везде' },
            { at: 'ribbed', text: 'рёбра по краям' },
            { at: 'note', text: 'край работает, середина отдыхает' },
        ],
    }),

    idea('Нельзя облегчать там, где деталь крепится или где приложена '
        + 'нагрузка: вокруг отверстий под винты материал оставляют '
        + 'полностью.', {
        diagram: 'printable',
        labels: [
            { at: 'thin', text: 'убрал материал у отверстия' },
            { at: 'thinFix', text: 'оставляй утолщение' },
            { at: 'note', text: 'вокруг крепежа не облегчают' },
        ],
    }),

    act('Облегчаем деталь вырезами, оставляя рёбра.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 80, d: 60, h: 20, shell: 4, label: 'Тело 1' }),
            ...doAction('scene'),
            select('part'),
            ...doAction('tool-shell'),
            setProp('Толщина', '4'),
            note('материал убран изнутри', { at: 'scene' }),
        ],
    }),

], { id: 'pr-43-optimize' });


/* ─── УРОК 44. Экспорт и подготовка к печати ─────────────────────── */

export const LESSON_EXPORT = lesson([

    idea('Модель готова — надо отдать её принтеру. Для этого её сохраняют '
        + 'в файл STL: он описывает только форму, без истории построения.', {
        diagram: 'printable',
        labels: [
            { at: 'note', text: 'STL — форма без истории операций' },
        ],
    }),

    idea('Перед печатью деталь разворачивают. От того, как она лежит '
        + 'на столе, зависит и прочность, и число поддержек: слои держатся '
        + 'друг за друга слабее, чем внутри слоя.', {
        diagram: 'printable',
        labels: [
            { at: 'over', text: 'лежит плохо — нужны поддержки' },
            { at: 'overFix', text: 'повернул — печатается сама' },
            { at: 'note', text: 'нагрузка должна идти вдоль слоёв' },
        ],
    }),

    act('Экспорт делается через меню «Файл»: выбираешь формат STL '
        + 'и сохраняешь.', {
        layout: 'full',
        steps: [
            spawn('part', { w: 70, d: 50, h: 30, shell: 2, label: 'Готово' }),
            select('part'),
            ...doAction('tab-solid'),
            note('файл готов для принтера', { at: 'scene' }),
        ],
    }),

    idea('Последняя проверка перед печатью — та же, что в конце третьего '
        + 'курса: толщина стенок и нависания. Две минуты проверки экономят '
        + 'три часа печати в мусор.', {
        diagram: 'printable',
        labels: [{ at: 'note', text: 'проверь толщины и нависания ещё раз' }],
    }),

], { id: 'pr-44-export' });


/* ─── УРОК 45. Итоговый проект ───────────────────────────────────── */

export const LESSON_FINAL = lesson([

    idea('Итоговая работа. Тема свободная, но требования обязательные: '
        + 'вещь должна быть нужной, печататься без поддержек и собираться, '
        + 'если деталей несколько.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'что-нибудь полезное' },
            { at: 'a2', text: 'решает реальную задачу' },
            { at: 'b', text: 'чтобы напечаталось' },
            { at: 'b2', text: 'стенки от 1,5 мм' },
            { at: 'note', text: 'свободная тема, жёсткие требования' },
        ],
    }),

    idea('Порядок работы — весь курс сразу: требования, набросок, измерения, '
        + 'модель, проверка. Ни один шаг не пропускай, даже если кажется, '
        + 'что и так понятно.', {
        diagram: 'sketch-solid',
        labels: [
            { at: 'sketch', text: 'требования и набросок' },
            { at: 'solid', text: 'модель и проверка' },
        ],
    }),

    idea('Защита работы — тоже часть задания. Надо объяснить: какую задачу '
        + 'решает вещь, откуда взялся каждый размер и почему деталь сделана '
        + 'именно так.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'мне так захотелось' },
            { at: 'a2', text: 'размер взят с предмета' },
            { at: 'note', text: 'каждое число должно иметь причину' },
        ],
    }),

    act('Перед сдачей проект прогоняют через проверку: выделяешь деталь, '
        + 'смотришь габариты и массу, обходишь навигатором в поисках '
        + 'нависаний.', {
        layout: 'full',
        steps: [
            spawn('final', { w: 70, d: 50, h: 30, shell: 2, fillet: 5,
                label: 'Твой проект' }),
            ...doAction('scene'),
            select('final'),
            setProp('Масса', '46 г'),
            ...doAction('viewcube'),
            note('проверка перед сдачей', { at: 'scene' }),
        ],
    }),

    result('Дальше — только практика. Всё, что нужно для настоящих деталей, '
        + 'ты уже умеешь.', {
        layout: 'full',
        steps: [
            spawn('final', { w: 70, d: 50, h: 30, shell: 2, fillet: 5,
                posts: [{ x: -22, y: -14, r: 4, h: 6 },
                    { x: 22, y: -14, r: 4, h: 6 }],
                label: 'Твой проект' }),
            select('final'),
            orbit(34),
            note('курс пройден', { at: 'scene' }),
        ],
    }),

], { id: 'pr-45-final' });


/* ─── УРОК 46. Разбор работ ──────────────────────────────────────── */

export const LESSON_REVIEW = lesson([

    idea('Последний урок — про то, как смотреть на свою работу со стороны. '
        + 'Это отдельный навык, и он важнее, чем кажется.', {
        diagram: 'brief',
        labels: [
            { at: 'a', text: 'вроде нормально' },
            { at: 'a2', text: 'проверил по списку' },
            { at: 'note', text: 'разбирают по требованиям, а не по ощущению' },
        ],
    }),

    idea('Первый вопрос: выполнены ли требования? Открой список и пройди '
        + 'по нему пунктами. Не «в целом да», а по каждому — да или нет.', {
        diagram: 'dims',
        labels: [{ at: 'note', text: 'каждое требование — отдельная галочка' }],
    }),

    idea('Второй вопрос: что сделал бы иначе? Обычно находится место, где '
        + 'деталь можно облегчить, упростить или собрать из меньшего числа '
        + 'частей. Это и есть рост.', {
        diagram: 'lighten',
        labels: [
            { at: 'solid', text: 'как сделал' },
            { at: 'ribbed', text: 'как можно лучше' },
            { at: 'note', text: 'вторая версия всегда лучше первой' },
        ],
    }),

    act('Открой свою работу и пройди по ней навигатором со всех сторон. '
        + 'Со стороны почти всегда видно то, чего не замечал, пока строил.', {
        layout: 'full',
        steps: [
            spawn('done', { w: 70, d: 50, h: 30, shell: 2, fillet: 5,
                label: 'Твоя работа' }),
            ...doAction('viewcube'),
            orbit(42),
            note('смотри со всех сторон', { at: 'scene' }),
        ],
    }),

    result('На этом программа кончается. Ты умеешь строить детали, читать '
        + 'чертежи, делать сборки и проектировать по заданию. Дальше — '
        + 'свои проекты.', {
        layout: 'full',
        steps: [
            spawn('done', { w: 70, d: 50, h: 30, shell: 2, fillet: 5,
                label: 'Готово' }),
            select('done'),
            orbit(36),
            note('спасибо за работу', { at: 'scene' }),
        ],
    }),

], { id: 'pr-46-review' });


/** Сценарии по id урока. Урок без сценария показывает старые анимации. */
export const SCENARIOS = {
    'fp-01-space': LESSON_SPACE,
    'fp-02-sketch': LESSON_SKETCH,
    'fp-03-extrude': LESSON_EXTRUDE,
    'fp-04-keychain': LESSON_KEYCHAIN,
    'fp-05-fillet': LESSON_FILLET,
    'fp-06-holes': LESSON_HOLES,
    'fp-07-shell': LESSON_SHELL,
    'fp-08-stand': LESSON_STAND,
    // Курс 2 «Читаем чертежи»
    'dr-09-views': LESSON_VIEWS,
    'dr-10-to-drawing': LESSON_TO_DRAWING,
    'dr-11-from-drawing': LESSON_FROM_DRAWING,
    'dr-12-hidden': LESSON_HIDDEN,
    'dr-13-dims': LESSON_DIMS,
    'dr-14-section': LESSON_SECTION,
    'dr-15-project': LESSON_DRAWING_PROJECT,
    // Курс 3 «Детали, которые собираются»
    'as-16-gap': LESSON_GAP,
    'as-17-fits': LESSON_FITS,
    'as-18-lid': LESSON_LID,
    'as-19-screws': LESSON_SCREWS,
    'as-20-slots': LESSON_SLOTS,
    'as-21-snap': LESSON_SNAP,
    'as-22-ribs': LESSON_RIBS,
    'as-23-case': LESSON_CASE,
    'as-24-print': LESSON_PRINT_CHECK,
    // Курс 4 «Тела вращения и массивы»
    'rv-25-revolve': LESSON_REVOLVE,
    'rv-26-funnel': LESSON_FUNNEL,
    'rv-27-pattern': LESSON_PATTERN,
    'rv-28-gear': LESSON_GEAR,
    'rv-29-gear-pair': LESSON_GEAR_PAIR,
    'rv-30-coil': LESSON_COIL,
    'rv-31-damper': LESSON_DAMPER,
    'rv-32-thread': LESSON_THREAD,
    'rv-33-sweep': LESSON_SWEEP,
    'rv-34-mechanism': LESSON_MECHANISM,
    // Курс 5 «Проектирование по заданию»
    'pr-35-brief': LESSON_BRIEF,
    'pr-36-idea': LESSON_IDEA,
    'pr-37-holder': LESSON_HOLDER,
    'pr-38-measure': LESSON_MEASURE,
    'pr-39-organizer': LESSON_ORGANIZER,
    'pr-40-split': LESSON_SPLIT,
    'pr-41-robot': LESSON_ROBOT,
    'pr-42-mass': LESSON_MASS,
    'pr-43-optimize': LESSON_OPTIMIZE,
    'pr-44-export': LESSON_EXPORT,
    'pr-45-final': LESSON_FINAL,
    'pr-46-review': LESSON_REVIEW,
};

export default SCENARIOS;
